import { useMemo, useRef } from "react"
import { first, maxBy } from "lodash"
import useCollectionTableData from "~/hooks/useCollectionTableData"
import ProductTableV2 from "../planning/ProductTable/ProductTableV2"
import { AggregationMethod } from "../reusable/AggregationSelector"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { useUser } from "~/providers/userProvider"
import { type SortColumn } from "../reusable/DataTableV2"
import {
  type ICollectionDetails,
  type ICollectionSimple,
  useGetCollections,
} from "~/hooks/useCollections"
import { filterNotUndefinedOrNull } from "~/utils/tsUtils"
import { isCollectionEnded } from "~/utils/collections"
import { useCollectionTableColumns } from "~/hooks/useCollectionTableColumns"
import useCollectionComparisonTableData from "~/hooks/useCollectionComparisonTableData"
import { useVariantMatches } from "~/hooks/useVariantMatches"
import { api } from "~/utils/api"
import { CollectionTableColumn } from "~/constants/collections"
import { ProductTableType } from "~/constants/productTable"
import { useVariantsByIds } from "~/hooks/useVariants"
import useShowInternalFeatures from "~/hooks/useShowInternalFeatures"
import { useCollectionBudgetData } from "~/hooks/useCollectionBudgetData"
import { useShownColumnIds } from "~/components/planning/ProductTable/useProductTableColumns"
import { useCompany } from "~/hooks/useCompany"
import { useCollectionSkuExtraDataKeys } from "~/hooks/useSku"
import { AgGridReact } from "ag-grid-react"
import {
  getTableColumns,
  TableColumnGroups,
} from "~/components/planning/ProductTable/ColumnChooser"
import { ProductTableColumn, resolveDefaultCollectionAggregation } from "~/utils/productTableUtils"
import ExportForecastPopover from "../forecast/ExportForecastPopover"

const aggregationOptions = [
  AggregationMethod.Variant,
  AggregationMethod.ProductName,
  AggregationMethod.Category,
  AggregationMethod.Brand,
  AggregationMethod.Gender,
  AggregationMethod.Color,
  AggregationMethod.Size,
  AggregationMethod.Vendor,
  AggregationMethod.ForecastModel,
]

type Props = {
  id: string
  variants: IVariantSummary[]
  collections: ICollectionDetails[]
  /** Override the default comparison (predecessor) collections */
  overrideComparisonCollections?: ICollectionSimple[]
  /** When true, includes variants that only exist in comparison collections (default: false) */
  includeComparisonOnlyVariants?: boolean
  selectableRows: boolean
  initialSortColumn?: SortColumn
  height?: string
  warehouseId?: string
  channel?: string
  isExpanded?: boolean
  onIsExpandedChange?: (isExpanded: boolean) => void
  enableFilter?: boolean
  demandPlanId?: string
  /** When true, automatically shows comparison columns if comparison data exists */
  autoShowComparisonColumns?: boolean
  /** Override the initial aggregation (supports dynamic aggregation strings) */
  initialAggregation?: AggregationMethod | string
  /** Override the default columns to display */
  overrideDefaultColumns?: CollectionTableColumn[]
  /** Additional aggregation options to add to the default list */
  additionalAggregationOptions?: AggregationMethod[]
  minHeight?: number
}

const CollectionTableDefaultColumnIds = [
  CollectionTableColumn.Sales,
  CollectionTableColumn.PreOrders,
]

const CollectionTableDefaultComparisonColumnIds = [
  CollectionTableColumn.BelongingToCollection,
  CollectionTableColumn.ProjectedSalesComparison,
  CollectionTableColumn.SalesComparison,
  CollectionTableColumn.PreOrdersComparison,
]

const BUDGET_COLUMN_IDS = [CollectionTableColumn.Budget]

export default function CollectionTable({
  id,
  collections,
  overrideComparisonCollections,
  includeComparisonOnlyVariants = false,
  variants,
  selectableRows,
  initialSortColumn,
  height,
  warehouseId,
  channel,
  isExpanded,
  onIsExpandedChange,
  enableFilter = false,
  autoShowComparisonColumns = false,
  initialAggregation,
  overrideDefaultColumns,
  additionalAggregationOptions,
  minHeight,
}: Props) {
  const { data: company } = useCompany()
  const collectionIds = useMemo(() => collections.map((c) => c.id), [collections])
  const endedCollectionIds = useMemo(
    () => collections.filter(isCollectionEnded).map((c) => c.id),
    [collections],
  )
  const externalCollectionIds = useMemo(
    () => collections.map((c) => c.externalId).filter((id): id is string => typeof id === "string"),
    [collections],
  )
  const variantIds = useMemo(() => variants.map((v) => v.variantId), [variants])
  const gridRef = useRef<AgGridReact>(null)

  // Auto-fetch predecessor collections if no override is provided
  const linkedCollectionIds = useMemo(
    () => filterNotUndefinedOrNull(collections.map((c) => c.predecessor?.id)),
    [collections],
  )
  const shouldFetchLinkedCollections =
    linkedCollectionIds.length > 0 && overrideComparisonCollections === undefined
  const { data: linkedCollections } = useGetCollections({
    collectionIds: shouldFetchLinkedCollections ? linkedCollectionIds : undefined,
  })

  const comparisonCollections =
    overrideComparisonCollections ?? (shouldFetchLinkedCollections ? linkedCollections : undefined)

  const { data: variantsMap } = api.collection.allVariantsForCollections.useQuery(
    {
      collectionIds,
      comparisonCollectionIds: comparisonCollections?.map((c) => c.id) ?? [],
    },
    { enabled: comparisonCollections !== undefined && comparisonCollections?.length > 0 },
  )

  const comparisonExternalCollectionIds = useMemo(
    () =>
      comparisonCollections
        ?.map((c) => c.externalId)
        .filter((id): id is string => typeof id === "string") ?? [],
    [comparisonCollections],
  )

  const { data: maps, isLoading: mapsLoading } = useCollectionTableData({
    variantIds,
    collectionIds,
    endedCollectionIds,
    warehouseId,
    channel,
    externalCollectionIds,
  })

  const comparisonOnlyVariantIds: string[] = variantsMap
    ? Array.from(variantsMap.entries())
        .filter(([, value]) => value === "comparison")
        .map(([key]) => key)
    : []
  const comparisonVariants = useVariantsByIds(comparisonOnlyVariantIds)
  const comparisonVariantIds = useMemo(
    () => comparisonVariants.map((v) => v.variantId),
    [comparisonVariants],
  )

  const hasComparisonEnabled = (comparisonCollections?.length ?? 0) > 0

  const { variantMatchesMap } = useVariantMatches({
    variantIds,
    enabled: hasComparisonEnabled && !includeComparisonOnlyVariants,
  })

  const endedComparisonCollectionIds = useMemo(
    () => (comparisonCollections ?? []).filter(isCollectionEnded).map((c) => c.id),
    [comparisonCollections],
  )

  const { data: comparisonMap, isLoading: comparisonLoading } = useCollectionComparisonTableData({
    variantIds: includeComparisonOnlyVariants
      ? [...variantIds, ...comparisonVariantIds]
      : variantIds,
    collectionIds: comparisonCollections?.map((c) => c.id) ?? [],
    endedCollectionIds: endedComparisonCollectionIds,
    channel,
    externalCollectionIds: comparisonExternalCollectionIds,
    // Comparison-only variants are rows here, so match-imputed values would duplicate them.
    // Gated at use, not only via `enabled`: a disabled query still returns cached matches.
    variantMatchesMap: includeComparisonOnlyVariants ? undefined : variantMatchesMap,
  })
  const defaultColumnIdsToDisplay = useMemo(() => {
    if (overrideDefaultColumns) {
      return overrideDefaultColumns
    }

    const columns = [...CollectionTableDefaultColumnIds]

    columns.push(CollectionTableColumn.ProjectedSales)
    columns.push(CollectionTableColumn.Forecast)
    columns.push(CollectionTableColumn.Out)

    const hasComparisonData =
      comparisonMap?.preOrderMap || comparisonMap?.salesMap || comparisonMap?.forecastMap
    if (autoShowComparisonColumns && hasComparisonData) {
      columns.push(...CollectionTableDefaultComparisonColumnIds)
    }

    return columns
  }, [comparisonMap, autoShowComparisonColumns, overrideDefaultColumns])

  // Budget data loads only while its column is shown.
  const budgetColumnVisible = useShownColumnIds(
    id,
    BUDGET_COLUMN_IDS,
    defaultColumnIdsToDisplay,
  ).has(CollectionTableColumn.Budget)
  const {
    budgetMap,
    hasBudget,
    isLoading: budgetLoading,
  } = useCollectionBudgetData({
    collectionIds,
    enabled: budgetColumnVisible,
  })

  const isDataLoading =
    mapsLoading ||
    (hasComparisonEnabled && comparisonLoading) ||
    (budgetColumnVisible && budgetLoading)

  const { data: collectionExtraDataFields } = useCollectionSkuExtraDataKeys()

  const { permissions } = useUser()
  const [showInternalFeatures] = useShowInternalFeatures()
  const showInDepthColumns = permissions.isStaff && showInternalFeatures

  const firstOriginal = maxBy(collections, (c) => c.skuCount) ?? first(collections)
  const originalName =
    collections !== undefined && collections?.length > 1
      ? firstOriginal?.collectionGroup?.name
      : firstOriginal?.name

  const extraColumns = useCollectionTableColumns({
    maps,
    comparisonMap,
    comparisonCollections,
    showInDepthColumns,
    originalName,
    variantsMap,
    collectionIds,
    budgetMap,
    hasBudget,
    includeComparisonOnlyVariants,
  })

  const finalAggregationOptions = useMemo(() => {
    if (!additionalAggregationOptions) return aggregationOptions
    return [...aggregationOptions, ...additionalAggregationOptions]
  }, [additionalAggregationOptions])

  const AvailableColumns: string[] = [
    ...extraColumns.columns.map((col) => col.colId).filter((str) => str !== undefined),
    ...(collectionExtraDataFields ?? []),
    ...getTableColumns()
      .filter(
        (col) =>
          col.group !== TableColumnGroups.SalesAndForecast &&
          col.group !== TableColumnGroups.ModelsAndAnalysis,
      )
      .map((col) => col.id),
  ]

  return (
    <div className="h-full">
      <ProductTableV2
        gridRef={gridRef}
        id={id}
        type={ProductTableType.Collection}
        height={height}
        minHeight={minHeight}
        variants={includeComparisonOnlyVariants ? [...variants, ...comparisonVariants] : variants}
        initialAggregation={
          initialAggregation
            ? (initialAggregation as unknown as AggregationMethod)
            : resolveDefaultCollectionAggregation(company?.defaultAggregationLevel)
        }
        overwriteColumnsToShow={AvailableColumns as unknown as Array<ProductTableColumn>}
        aggregationOptions={finalAggregationOptions}
        initialSortColumn={initialSortColumn}
        defaultColumnIdsToDisplay={defaultColumnIdsToDisplay}
        selectableRows={selectableRows}
        extraColumns={extraColumns}
        topRightComponent={
          <ExportForecastPopover variants={variants} gridRef={gridRef} showSizeSplit />
        }
        enableSearch
        enableFilter={enableFilter}
        groupAggFiltering={false}
        collectionIds={collectionIds}
        overwriteCollectionColumns
        isExpanded={isExpanded}
        onIsExpandedChange={onIsExpandedChange}
        loading={isDataLoading}
      />
    </div>
  )
}
